import publicRoutes from '../data/public-routes.json';
import socialCards from '../data/social-cards.json';

/** Editorial URLs always identify production, including in a noindex Preview. */
export const SEO_BASE_URL = 'https://ancelade.cloud';
export const SEO_IMAGE_URL = `${SEO_BASE_URL}/og/${socialCards.version}/fr/home.png`;
export const SEO_LANGUAGES: readonly string[] = publicRoutes.languages;

export interface SeoData {
  title: string;
  description: string;
  name?: string;
  pageType?: 'WebPage' | 'CollectionPage' | 'AboutPage' | 'ContactPage';
  serviceType?: string;
  ogType?: string;
  noindex?: boolean;
}

export interface SeoPageContext {
  language: string;
  path: string;
  canonical: string;
  languages: readonly string[];
  publicPage: boolean;
  /** Listed in the sitemap: a manifest page marked `indexable: false` is served but kept out of search results. */
  indexable: boolean;
}

export interface LocalizedSeo {
  title: string;
  description: string;
  name: string;
  homeName: string;
}

/** A question and answer exactly as displayed in the page's FAQ section. */
export interface LocalizedFaqEntry {
  question: string;
  answer: string;
}

export const OG_LOCALES: Readonly<Record<string, string>> = {
  fr: 'fr_FR',
  en: 'en_GB',
  es: 'es_ES',
  it: 'it_IT',
  de: 'de_DE',
  pt: 'pt_PT',
  nl: 'nl_NL',
  pl: 'pl_PL',
  ro: 'ro_RO',
};

/** Product cards follow the page and locale; institutional and unknown pages use the localized brand card. */
export function socialImageForPage(page: Pick<SeoPageContext, 'language' | 'path'>): {
  url: string;
  width: number;
  height: number;
  copyKey: string;
} {
  const cards: Readonly<Record<string, string>> = socialCards.pages;
  const language = SEO_LANGUAGES.includes(page.language) ? page.language : 'fr';
  const path = Object.hasOwn(cards, page.path) ? page.path : '';
  return {
    url: `${SEO_BASE_URL}/og/${socialCards.version}/${language}/${cards[path]}.png`,
    width: socialCards.width,
    height: socialCards.height,
    copyKey: path ? `seo.pages.${path}.name` : 'seo.pages.home.title',
  };
}

export function localizedSeoUrl(language: string, path: string): string {
  const suffix = path ? `/${path}` : '';
  return `${SEO_BASE_URL}/${language}${suffix}`;
}

/** Product filters and Angular matrix parameters do not identify separate pages. */
export function seoPageContext(routerUrl: string, preferredLanguage = 'fr'): SeoPageContext {
  const segments = routerUrl
    .split(/[?#]/, 1)[0]
    .split('/')
    .filter(Boolean)
    .map((segment) => segment.split(';', 1)[0]);
  const root = segments.length === 0;
  const language = SEO_LANGUAGES.includes(segments[0]) ? segments.shift()! : preferredLanguage;
  const path = segments.join('/');
  const page = publicRoutes.pages.find((candidate) => candidate.path === path);
  const languages = page?.languages ?? SEO_LANGUAGES;
  const publicPage = !!page && languages.includes(language);

  return {
    language,
    path,
    canonical: root ? `${SEO_BASE_URL}/` : localizedSeoUrl(language, path),
    languages: page ? languages : [],
    publicPage,
    indexable: publicPage && page.indexable !== false,
  };
}

/** Plain metadata remains safe even if a translation accidentally contains markup. */
export function plainSeoText(value: string): string {
  let result = '';
  let start = 0;
  for (;;) {
    const open = value.indexOf('<', start);
    if (open === -1) break;
    const close = value.indexOf('>', open + 1);
    if (close === -1) break;
    result += `${value.slice(start, open)} `;
    start = close + 1;
  }
  return (result + value.slice(start)).replace(/\s+/g, ' ').trim();
}

export function brandSeoTitle(value: string): string {
  let title = plainSeoText(value);
  const brand = 'ancelade';
  while (title.toLowerCase().endsWith(brand)) {
    const beforeBrand = title.slice(0, -brand.length).trimEnd();
    if (!beforeBrand.endsWith('|')) break;
    title = beforeBrand.slice(0, -1).trimEnd();
  }
  return title.toLowerCase() === brand ? 'Ancelade' : `${title} | Ancelade`;
}

/** One connected graph, using only facts represented on the public pages. */
export function buildSeoGraph(
  seo: SeoData,
  text: LocalizedSeo,
  page: SeoPageContext,
  faq: readonly LocalizedFaqEntry[] = []
): Record<string, unknown> {
  const organizationId = `${SEO_BASE_URL}/#organization`;
  const websiteId = `${SEO_BASE_URL}/#website`;
  const webpageId = `${page.canonical}#webpage`;
  const serviceId = `${page.canonical}#service`;
  const breadcrumbId = `${page.canonical}#breadcrumb`;
  const faqId = `${page.canonical}#faq`;
  const image = socialImageForPage(page);
  const webpage: Record<string, unknown> = {
    '@type': seo.pageType ?? 'WebPage',
    '@id': webpageId,
    url: page.canonical,
    name: text.title,
    description: text.description,
    inLanguage: page.language,
    isPartOf: { '@id': websiteId },
    publisher: { '@id': organizationId },
    primaryImageOfPage: { '@type': 'ImageObject', url: image.url, width: image.width, height: image.height },
  };
  const graph: Record<string, unknown>[] = [
    {
      '@type': 'Organization',
      '@id': organizationId,
      name: 'Ancelade',
      legalName: 'ANCELADE',
      alternateName: 'Private Heberg',
      url: SEO_BASE_URL,
      logo: { '@type': 'ImageObject', url: `${SEO_BASE_URL}/brand/ancelade-logo.svg` },
      email: 'contact@ancelade.com',
      telephone: '+33184754308',
      identifier: { '@type': 'PropertyValue', propertyID: 'SIREN', value: '918495201' },
      address: {
        '@type': 'PostalAddress',
        streetAddress: '128 rue La Boétie',
        postalCode: '75008',
        addressLocality: 'Paris',
        addressCountry: 'FR',
      },
      contactPoint: [
        { '@type': 'ContactPoint', contactType: 'sales', telephone: '+33184754308', email: 'contact@ancelade.com' },
        { '@type': 'ContactPoint', contactType: 'technical support', email: 'support@ancelade.com' },
      ],
      sameAs: ['https://www.peeringdb.com/org/31453', 'https://fr.trustpilot.com/review/privateheberg.net'],
    },
    {
      '@type': 'WebSite',
      '@id': websiteId,
      name: 'Ancelade',
      url: SEO_BASE_URL,
      inLanguage: [...SEO_LANGUAGES],
      publisher: { '@id': organizationId },
    },
    webpage,
  ];

  if (page.path) {
    webpage['breadcrumb'] = { '@id': breadcrumbId };
    graph.push({
      '@type': 'BreadcrumbList',
      '@id': breadcrumbId,
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: text.homeName, item: localizedSeoUrl(page.language, '') },
        { '@type': 'ListItem', position: 2, name: text.name, item: page.canonical },
      ],
    });
  }
  if (seo.serviceType) {
    webpage['mainEntity'] = { '@id': serviceId };
    graph.push({
      '@type': 'Service',
      '@id': serviceId,
      name: text.name,
      description: text.description,
      serviceType: text.name,
      url: page.canonical,
      provider: { '@id': organizationId },
      mainEntityOfPage: { '@id': webpageId },
    });
  } else if (seo.pageType === 'AboutPage' || seo.pageType === 'ContactPage') {
    webpage['about'] = { '@id': organizationId };
  }
  if (faq.length) {
    // A separate node keeps the page type; the questions are those displayed in the page's FAQ section.
    webpage['hasPart'] = { '@id': faqId };
    graph.push({
      '@type': 'FAQPage',
      '@id': faqId,
      url: page.canonical,
      inLanguage: page.language,
      isPartOf: { '@id': webpageId },
      mainEntity: faq.map((entry) => ({
        '@type': 'Question',
        name: entry.question,
        acceptedAnswer: { '@type': 'Answer', text: entry.answer },
      })),
    });
  }

  return { '@context': 'https://schema.org', '@graph': graph };
}

/** Escape HTML raw-text delimiters before SSR serializes the script element. */
export function serializeSeoGraph(graph: Record<string, unknown>): string {
  return JSON.stringify(graph).replaceAll('<', String.raw`\u003c`);
}
